import { useCallback, useEffect, useMemo, useState } from 'react';
import { ExpandableSection } from 'foundations-components/transitional/components/ExpandableSection';
import useFetch from 'framework-asset-preview-lib/internal/shared/hooks/useFetch';
import { fetchCrmObjects } from 'framework-asset-preview-lib/internal/shared/api/crm';
import { fetchAssociatedObjects } from 'framework-asset-preview-lib/internal/shared/api/associations';
import useFetchCrmObjectProperties from 'framework-asset-preview-lib/internal/shared/hooks/useFetchCrmObjectProperties';
import useAssetPreviewTracking from 'framework-asset-preview-lib/internal/shared/hooks/useAssetPreviewTracking';
import cachedHttp from 'framework-asset-preview-lib/internal/shared/api/cachedHttp';
import assetPreviewObserver from 'framework-asset-preview-lib/utils/assetPreviewObserver';
import { REFETCH_ASSOCIATIONS } from 'framework-asset-preview-lib/utils/constants';
import AssociatedAssets from './AssociatedAssets';
import AssetPaginator from './AssetPaginator';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export default function AssociationGroupContainer({
  associationGroup,
  crmObject,
  defaultOpen,
  hoverAssetId,
  onHoverAsset
}) {
  const [open, setOpen] = useState(defaultOpen);
  const [currentPage, setCurrentPage] = useState(1);
  const [offsetMap, setOffsetMap] = useState({
    1: {
      currentOffset: 0,
      previousOffset: 0
    }
  });
  const {
    trackAssetPreviewInteraction
  } = useAssetPreviewTracking();
  const fetchAssociatedObjectsProps = useMemo(() => ({
    fetcher: () => fetchAssociatedObjects({
      objectId: crmObject.objectId,
      associationDefinitions: associationGroup.associationDefinitions,
      offset: offsetMap[currentPage].currentOffset
    }),
    skip: !open
  }), [associationGroup.associationDefinitions, crmObject.objectId, currentPage, offsetMap, open]);
  const {
    data: associatedObjects,
    error: errorAssociations,
    loading: loadingAssociations,
    refetch: refetchAssociatedObjects
  } = useFetch(fetchAssociatedObjectsProps);
  const skipCrmSearch = useMemo(() => Boolean(!associatedObjects || associatedObjects.results.length === 0 || loadingAssociations || errorAssociations), [associatedObjects, errorAssociations, loadingAssociations]);
  const fetchCrmObjectsProps = useMemo(() => ({
    fetcher: () => fetchCrmObjects(associationGroup.toObjectTypeId, {
      filterGroups: [{
        filters: [{
          operator: 'IN',
          property: 'hs_object_id',
          values: associatedObjects ? associatedObjects.results.map(objectId => String(objectId)) : []
        }]
      }]
    }),
    skip: skipCrmSearch
  }), [associatedObjects, associationGroup.toObjectTypeId, skipCrmSearch]);
  const {
    called: calledCrmObjects,
    data,
    error: errorCrmObjects,
    loading: loadingCrmObjects
  } = useFetch(fetchCrmObjectsProps);
  const {
    properties,
    error: errorProperties,
    loading: loadingProperties
  } = useFetchCrmObjectProperties(associationGroup.toObjectTypeId, {
    skip: skipCrmSearch
  });
  const handleOpenChange = useCallback(event => {
    const isOpen = Boolean(event.target.value);
    setOpen(event.target.value);
    trackAssetPreviewInteraction({
      action: isOpen ? 'expand association group' : 'expand association group'
    });
  }, [trackAssetPreviewInteraction]);
  const isLoading = useMemo(() => loadingAssociations || loadingCrmObjects || loadingProperties || !calledCrmObjects && !skipCrmSearch, [calledCrmObjects, loadingAssociations, loadingCrmObjects, loadingProperties, skipCrmSearch]);
  const isError = useMemo(() => !isLoading && Boolean(errorAssociations || errorCrmObjects || errorProperties), [errorAssociations, errorCrmObjects, errorProperties, isLoading]);
  const isEmptyState = useMemo(() => !isLoading && !isError && (!data || data.results.length === 0), [data, isError, isLoading]);
  const refetchAssociations = useCallback(() => {
    cachedHttp.wipeAll();
    refetchAssociatedObjects();
  }, [refetchAssociatedObjects]);
  useEffect(() => {
    assetPreviewObserver.on(REFETCH_ASSOCIATIONS, refetchAssociations);
    return () => assetPreviewObserver.off(REFETCH_ASSOCIATIONS, refetchAssociations);
  }, [refetchAssociations]);
  return /*#__PURE__*/_jsxs(ExpandableSection, {
    indentation: "compact",
    titleText: associationGroup.objectTypeName,
    open: open,
    onOpenChange: handleOpenChange,
    "data-test-id": `expandable-asset-section-${associationGroup.toCrmObjectTypeDefinition.name}`,
    children: [/*#__PURE__*/_jsx(AssociatedAssets, {
      associatedObjects: data ? data.results : [],
      associationGroup: associationGroup,
      crmObject: crmObject,
      hoverAssetId: hoverAssetId,
      isEmptyState: isEmptyState,
      isError: isError,
      isLoading: isLoading,
      onHoverAsset: onHoverAsset,
      properties: properties
    }), data && data.results.length ? /*#__PURE__*/_jsx(AssetPaginator, {
      isNextDisabled: Boolean(!associatedObjects || !associatedObjects.hasMore),
      isPrevDisabled: currentPage === 1,
      onNextClick: () => {
        if (!associatedObjects) {
          return;
        }
        setCurrentPage(curr => curr + 1);
        setOffsetMap(prev => Object.assign({}, prev, {
          [currentPage + 1]: {
            currentOffset: associatedObjects.offset,
            previousOffset: offsetMap[currentPage].currentOffset
          }
        }));
      },
      onPrevClick: () => {
        setCurrentPage(curr => curr - 1);
      }
    }) : null]
  });
}